import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import debounce from 'lodash.debounce';
import { useParams } from 'react-router-dom'
import LabCard from './LabCard';
import TestCard from './TestCard';
import { getConfig } from '../../config.js';
const themeColors = {
  primary: '#31C48D',
  secondary: '#007BFF',
  text: '#4D4D4D',
  background: '#F9FAFB',
  white: '#FFFFFF',
  grey: '#686978',
  green: '#31C48D',
};

// API Base URL
const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:8080';

const LabDetailScreen = () => {
  const { labId } = useParams(); // Get lab ID from the URL params
  const [labData, setLabData] = useState(null);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTests, setLoadingTests] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [cart, setCart] = useState([]); // Cart for added tests

  // Fetch Lab Details
  useEffect(() => {
    const fetchLabDetails = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/labs/${labId}?latitude=26.8505899&longitude=75.7909157`
        );
        if (response.data.status) {
          setLabData(response.data.data);
        } else {
          setError('Failed to fetch lab details.');
        }
      } catch (err) {
        setError('Something went wrong. Please try again!');
      } finally {
        setLoading(false);
      }
    };

    fetchLabDetails();
  }, [labId]);

  // Fetch Tests
  const fetchTests = async (searchQuery = '') => {
    setLoadingTests(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/lab-items?lab-id=${labId}&query=${searchQuery}&offset=0&limit=10`
      );
      if (response.data.status) {
        setTests(response.data.data || []);
      } else {
        setError('Failed to fetch tests.');
      }
    } catch (err) {
      setError('Something went wrong while fetching tests!');
    } finally {
      setLoadingTests(false);
    }
  };

  // Debounced Search Handler
  const handleSearch = debounce((value) => {
    fetchTests(value);
  }, 500);

  useEffect(() => {
    fetchTests();
  }, [labId]);

  const addToCart = (test) => {
    setCart((prevCart) => [...prevCart, test]);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ padding: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 3, backgroundColor: themeColors.background, minHeight: '100vh' }}>
      <LabCard labData={labData} themeColors={themeColors} />

      {/* Search Bar */}
      <TextField
        fullWidth
        placeholder="Search tests (e.g., CBC)"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          handleSearch(e.target.value);
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ marginBottom: 3, backgroundColor: themeColors.white }}
      />

      {/* Test List */}
      {loadingTests ? (
        <CircularProgress />
      ) : (
        <Grid container spacing={2}>
          {tests.map((test) => (
            <Grid item xs={12} sm={6} md={4} key={test.reference_id}>
              <TestCard test={test} themeColors={themeColors} addToCart={addToCart} />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default LabDetailScreen;